import React from "react";
import { useTranslation } from "react-i18next";
import { DeviceCommandProvider } from "src/providers/DeviceCommandProvider";
import { useAppSettingsService } from "src/services/appSettingsService/appSettingsService";
import { useDispatch, useSelector } from "src/store";

import { License } from "@dashboard/devices/types";
import { getSummaryIndicatorData } from "@dashboard/devices/utils/license";
import CommandsTab from "@dashboard/shared/components/SidePanel/LicensePanel/CommandsTab/CommandsTab";
import { useCurrentSubscription } from "@dashboard/workspaces/api/helpers";
import { closeDetails, selectDetailsLicense, selectPaneTab, setPaneTab } from "@devices/store";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { Box, Typography, useTheme } from "@mui/material";
import { ScreenViewButton } from "@screenView/ui/ScreenViewButton/ScreenViewButton";
import { getSettings } from "@services/configService";
import { SecondaryButton } from "@shared/CustomButton";
import { Tooltip } from "@shared/Tooltip";

import { MenuLicense } from "../shared/MenuLicense";
import { PaneElement } from "../shared/PaneElement";
import { useStyle } from "../shared/style";
import { Tab } from "../shared/Tab";

import AppTab from "./AppTab/AppTab";
import BwcTab from "./BwcTab/BwcTab";
import EventTab from "./EventTab/EventTab";
import FileTab from "./FileTab/FileTab";
import HardwareTab from "./HardwareTab/HardwareTab";
import { LogButton } from "./LogButton/LogButton";
import NetworkTab from "./NetworkTab/NetworkTab";
import DeviceOverviewTab from "./OverviewTab/DeviceOverviewTab";
import LicenseOverviewTab from "./OverviewTab/LicenseOverviewTab";
import SystemTab from "./SystemTab/SystemTab";
import LicenseTab from "./LicenseTab";
import { VncButton } from "./VncButon";

const generalTabs = ["OVERVIEW", "LICENSE"];
const deviceTabs = ["APPS", "FILES", "COMMANDS", "EVENTS"];
const deviceStateTabs = ["OS", "HARDWARE", "NETWORK", "BWC"];

function getLicenseSubtitle(license: License | null): string | null {
    if (!license) {
        return null;
    }

    if (license.device?.description) {
        return license.device.description;
    }

    if (!license.device) {
        if (license.comment) {
            return license.comment;
        }
        if (license.activationCode) {
            return license.activationCode;
        }
    }

    return null;
}

export const LicenseSidePanel = () => {
    const license = useSelector(selectDetailsLicense);
    const dispatch = useDispatch();
    const { classes } = useStyle();
    const { t } = useTranslation(["sidepanels", "common"]);
    const { features } = getSettings();
    const theme = useTheme();
    const device = license?.device;
    const { effectiveWorkspaceSettings } = useAppSettingsService();
    const filesTabAvailable =
        features.licensePanelTabs.filesEnabled && effectiveWorkspaceSettings.isFilesPageEnabled && !!device?.lastState;

    const tabs = [...generalTabs, ...(license?.device ? deviceTabs : []), ...(license?.device?.lastState ? deviceStateTabs : [])];

    const value = useSelector(selectPaneTab);
    const isLicenseTab = tabs.includes(value);

    const { data: subscription } = useCurrentSubscription({});
    const isValid = !!subscription?.isValid;
    const isExpired = !!subscription?.isExpired;
    const statusIndicator = getSummaryIndicatorData(license, isExpired, theme, isValid);

    const subtitle = getLicenseSubtitle(license);

    const onChange = (_event: React.ChangeEvent<object>, tabTitle: string) => {
        dispatch(setPaneTab(tabTitle));
    };

    React.useEffect(() => {
        if (!isLicenseTab) {
            dispatch(setPaneTab("OVERVIEW"));
        }
    }, [dispatch, isLicenseTab]);

    if (!isLicenseTab || !license) {
        return null;
    }

    return (
        <DeviceCommandProvider>
            <TabContext value={value}>
                <PaneElement.HeaderBox>
                    <Box className={classes.headerBox}>
                        <Box className={classes.licenseSummaryBox}>
                            <Tooltip title={statusIndicator.tooltip}>
                                <Box
                                    className={classes.licenseSummaryIndicator}
                                    style={{ backgroundColor: statusIndicator.color }}
                                />
                            </Tooltip>
                            <Typography variant="h6" className={classes.panelTitle}>
                                {license.device?.name || t("sidepanels:devicePanel.overviewTab.deviceSlot")}
                            </Typography>
                        </Box>
                        {subtitle && (
                            <Typography variant="body1" className={classes.panelSubtitle}>
                                {subtitle}
                            </Typography>
                        )}
                    </Box>

                    <Box className={classes.buttonBox}>
                        <LogButton {...license} />
                        <VncButton {...license} />
                        <ScreenViewButton {...license} />
                        <MenuLicense {...license} />
                        <SecondaryButton onClick={() => dispatch(closeDetails())}>
                            <i className="fas fa-times" />
                        </SecondaryButton>
                    </Box>
                </PaneElement.HeaderBox>

                <TabList onChange={onChange} variant="scrollable" scrollButtons>
                    <Tab
                        value="OVERVIEW"
                        label={t("sidepanels:devicePanel.overviewTab.title")}
                        icon={<i className="fas fa-tachometer-alt" />}
                    />
                    {features.licensePanelTabs.licenseEnabled ? (
                        <Tab
                            value="LICENSE"
                            label={t("sidepanels:devicePanel.licenseTab.title")}
                            icon={<i className="fas fa-desktop" />}
                        />
                    ) : null}

                    {features.licensePanelTabs.appsEnabled && license.device ? (
                        <Tab value="APPS" label={t("sidepanels:devicePanel.appsTab.title")} icon={<i className="fas fa-th" />} />
                    ) : null}
                    {features.licensePanelTabs.bwcEnabled && license.device?.lastState ? (
                        <Tab value="BWC" label={t("sidepanels:devicePanel.bwcTab.title")} icon={<i className="fas fa-video" />} />
                    ) : null}
                    {filesTabAvailable ? (
                        <Tab
                            value="FILES"
                            label={t("sidepanels:devicePanel.filesTab.title")}
                            icon={<i className="fa-solid fa-file" />}
                        />
                    ) : null}

                    {features.licensePanelTabs.osEnabled && license.device?.lastState ? (
                        <Tab value="OS" label={t("sidepanels:devicePanel.osTab.title")} icon={<i className="fas fa-desktop" />} />
                    ) : null}
                    {features.licensePanelTabs.hardwareEnabled && license.device?.lastState ? (
                        <Tab
                            value="HARDWARE"
                            label={t("sidepanels:devicePanel.hardwareTab.title")}
                            icon={<i className="fas fa-microchip" />}
                        />
                    ) : null}
                    {features.licensePanelTabs.networkEnabled && license.device?.lastState ? (
                        <Tab
                            value="NETWORK"
                            label={t("sidepanels:devicePanel.networkTab.title")}
                            icon={<i className="fas fa-network-wired" />}
                        />
                    ) : null}
                    {features.licensePanelTabs.eventsEnabled && license.device ? (
                        <Tab value="EVENTS" label={t("common:label.events")} icon={<i className="fas fa-bell" />} />
                    ) : null}
                    {features.licensePanelTabs.commandsEnabled && license.device ? (
                        <Tab
                            value="COMMANDS"
                            label={t("sidepanels:devicePanel.commandsTab.title")}
                            icon={<i className="fas fa-terminal" />}
                        />
                    ) : null}
                </TabList>

                <Box className={classes.contentBox}>
                    <TabPanel value="OVERVIEW">
                        {license.device ? <DeviceOverviewTab {...license} /> : <LicenseOverviewTab {...license} />}
                    </TabPanel>
                    {features.licensePanelTabs.licenseEnabled ? (
                        <TabPanel value="LICENSE">
                            <LicenseTab {...license} />
                        </TabPanel>
                    ) : null}
                    {features.licensePanelTabs.osEnabled && license.device && license.device.lastState ? (
                        <TabPanel value="OS">
                            <SystemTab deviceState={license.device.lastState} license={license} />
                        </TabPanel>
                    ) : null}
                    {features.licensePanelTabs.hardwareEnabled && license.device && license.device.lastState ? (
                        <TabPanel value="HARDWARE">
                            <HardwareTab deviceState={license.device.lastState} />
                        </TabPanel>
                    ) : null}
                    {features.licensePanelTabs.networkEnabled && license.device && license.device.lastState ? (
                        <TabPanel value="NETWORK">
                            <NetworkTab deviceId={license.device.id} deviceState={license.device.lastState} />
                        </TabPanel>
                    ) : null}
                    {features.licensePanelTabs.appsEnabled && license.device ? (
                        <TabPanel value="APPS">
                            <AppTab device={license.device} groupId={license.groupId} />
                        </TabPanel>
                    ) : null}
                    {features.licensePanelTabs.bwcEnabled && license.device?.lastState ? (
                        <TabPanel value="BWC">
                            <BwcTab device={license.device} />
                        </TabPanel>
                    ) : null}
                    {filesTabAvailable && device ? (
                        <TabPanel value="FILES">
                            <FileTab device={device} groupId={license.groupId} />
                        </TabPanel>
                    ) : null}
                    {features.licensePanelTabs.commandsEnabled && license.device ? (
                        <TabPanel value="COMMANDS">
                            <CommandsTab device={license.device} />
                        </TabPanel>
                    ) : null}
                    {features.licensePanelTabs.eventsEnabled && license.device ? (
                        <TabPanel value="EVENTS">
                            <EventTab device={license.device} />
                        </TabPanel>
                    ) : null}
                </Box>
            </TabContext>
        </DeviceCommandProvider>
    );
};
